import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { ChevronDown, Search } from 'lucide-react';

export const SearchableSelect = forwardRef(({ 
  label, 
  value, 
  onChange, 
  options = [], 
  placeholder = "Select Option",
  error,
  readOnly,
  name,
  className,
  onBlur,
  allowCustom = false
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Helper to get consistent label/value
  const getOptLabel = (opt) => typeof opt === 'object' ? (opt.label || opt.name || opt.title || opt.vendorName || String(opt.value)) : String(opt);
  const getOptDropdownLabel = (opt) => typeof opt === 'object' && opt.dropdownLabel ? opt.dropdownLabel : getOptLabel(opt);
  const getOptValue = (opt) => typeof opt === 'object' ? opt.value : opt;

  const selectedOpt = options.find(opt => getOptValue(opt) === value);
  const selectedLabel = selectedOpt ? getOptLabel(selectedOpt) : (value || "");

  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options.filter(opt => 
    getOptLabel(opt).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={`space-y-1.5 ${className || ""} ${isOpen ? 'relative z-50' : ''}`} ref={wrapperRef}>
      {label && (
        <label className="flex items-center gap-1.5 text-[13px] font-normal text-slate-500">
          {label}
        </label>
      )}
      <div className="relative">
        <div
          className={`flex items-center w-full border rounded-md px-3 appearance-none text-slate-800 transition-all bg-white min-h-[35px] text-[13px] ${
            error ? 'border-red-300 focus-within:border-red-500 focus-within:ring-red-200 focus-within:ring-1' : 'border-slate-200 focus-within:border-blue-500 focus-within:ring-blue-500/30 focus-within:ring-1'
          } ${readOnly ? 'bg-slate-100 opacity-60' : 'cursor-text'}`}
          onClick={() => {
            if (!readOnly) {
              setIsOpen(true);
            }
          }}
        >
          {isOpen ? (
            <input
              ref={ref}
              type="text"
              className="w-full bg-transparent outline-none h-[33px]"
              placeholder="Type to search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          ) : (
            <div className={`w-full h-[33px] flex items-center ${!selectedLabel ? 'text-slate-400' : ''}`}>
              {selectedLabel || placeholder}
            </div>
          )}
        </div>
        <ChevronDown
          size={16}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
        />
        {isOpen && (
          <ul className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-[150px] overflow-y-auto text-[13px] py-1 custom-scrollbar">
            {filteredOptions.length > 0 && (
              filteredOptions.map((opt, idx) => {
                const val = getOptValue(opt);
                const lbl = getOptDropdownLabel(opt);
                return (
                  <li
                    key={typeof val === 'string' || typeof val === 'number' ? val : idx}
                    className={`px-3 py-2 text-[12px] font-medium cursor-pointer transition-colors hover:bg-[#1860e6] hover:text-white ${value === val ? 'bg-[#1860e6] text-white' : 'text-slate-700'}`}
                    onMouseDown={(e) => {
                      e.preventDefault(); // Prevent input blur
                      if (typeof onChange === 'function') onChange(val);
                      setIsOpen(false);
                      setSearchTerm("");
                    }}
                  >
                    {lbl}
                  </li>
                );
              })
            )}
            
            {allowCustom && searchTerm.trim() && !options.some(opt => getOptLabel(opt).toLowerCase() === searchTerm.trim().toLowerCase()) && (
              <li
                className="px-3 py-2 text-[12px] font-bold text-indigo-600 cursor-pointer transition-colors hover:bg-indigo-50"
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (typeof onChange === 'function') onChange(searchTerm.trim());
                  setIsOpen(false);
                  setSearchTerm("");
                }}
              >
                + Add "{searchTerm.trim()}"
              </li>
            )}
            
            {filteredOptions.length === 0 && (!allowCustom || !searchTerm.trim()) && (
              <li className="px-3 py-2 text-slate-500 italic">No exact match found.</li>
            )}
          </ul>
        )}
      </div>
      {error && <p className="text-[12px] text-red-500">{error}</p>}
    </div>
  );
});

SearchableSelect.displayName = 'SearchableSelect';
